import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EQueryKeys } from 'constants/enums';
import { getPublisherIdByPortalId } from 'utils/portalUtils';

import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';
import { useUserDetails } from './useUserDetails';

export enum ECredentialsType {
  MAIN_KEY = 'MAIN_KEY',
  CHECKOUT_PUBLIC_KEY = 'CHECKOUT_PUBLIC_KEY',
  PUBLISHER_TOKEN = 'PUBLISHER_TOKEN'
}

export interface ICredential {
  id: string;
  type: ECredentialsType;
  value: string;
  updatedAt: string;
  createdBy: string;
}

export default function useCredentials(currentPublisherId?: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { portalId } = useParams();
  const { userDetails } = useUserDetails();
  const effectivePublisherId =
    currentPublisherId ||
    (portalId ? getPublisherIdByPortalId(portalId, userDetails) : undefined);
  const publisherHeader = effectivePublisherId
    ? { 'x-publishers': effectivePublisherId }
    : undefined;
  const credentialsQueryKey = [EQueryKeys.CREDENTIALS, effectivePublisherId];

  const getCredentials = useQuery<ICredential[]>({
    ...fetchConfig.general,
    queryKey: credentialsQueryKey,
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.CREDENTIALS),
        {},
        publisherHeader
      );
    },
    enabled: !!effectivePublisherId
  });

  const rotateCredential = useMutation(
    async (credentialsType: ECredentialsType) => {
      return await axios.post(
        getApiRouteOrMock(EApiRoutes.CREDENTIALS),
        { type: credentialsType },
        publisherHeader
      );
    }
  );

  const revokeCredential = useMutation(
    async ({
      credentialId,
      credentialType
    }: {
      credentialId: string;
      credentialType: ECredentialsType;
    }) => {
      return await axios.patch(
        `${getApiRouteOrMock(EApiRoutes.CREDENTIALS)}/${credentialId}/revoke?type=${credentialType}`,
        {},
        publisherHeader
      );
    },
    {
      onSuccess: async () => {
        const { data } = await getCredentials.refetch();

        if (data) {
          queryClient.setQueryData(credentialsQueryKey, data);
        }
      }
    }
  );

  return {
    getCredentials,
    rotateCredential,
    revokeCredential
  };
}
